// GameLogSection — per-player game log (Tier 5 #1).
//
// Fetches GET /api/players/:slug/games (public) with an optional season
// filter and offset pagination (page size 50, server-fixed). Rendered by
// PlayerProfile ONLY for players with an api_sports_player_id — profiles
// without one (agent-created, family test profiles) never mount this, so
// they can't break; players WITH an id but no harvested rows get the
// empty state.
//
// Formatting: NULL → "—" everywhere (never 0). Shooting cells show
// "makes-attempts · pct" — the m-a display makes low-volume percentages
// self-evident (a 1-for-1 100% FT game reads "1-1 · 100%"). A pathology
// row (makes > attempts) arrives with pct null from the server and
// renders "13-6 · —", never >100%. Percentages arrive as 0–1 fractions
// per the locked convention.
//
// Mobile: horizontal scroll with a sticky Date column (Compare's
// sticky-first-column pattern). 3x3 rows carry an inline badge.

import { useEffect, useState } from 'react'

interface GameRow {
  api_game_id: number
  season: string
  date: string | null
  league: string | null
  is_3x3: boolean
  team: string | null
  home_team: string | null
  away_team: string | null
  home_score: number | null
  away_score: number | null
  is_home: boolean | null
  opponent: string | null
  minutes: number | null
  pts: number | null
  reb: number | null
  ast: number | null
  fg2m: number | null
  fg2a: number | null
  tpm: number | null
  tpa: number | null
  ftm: number | null
  fta: number | null
  fg_pct: number | null
  three_pct: number | null
  ft_pct: number | null
}

interface GamesResponse {
  player: { slug: string; name: string }
  games: GameRow[]
  has_more: boolean
}

interface Props {
  playerSlug: string
  // Distinct non-3x3 seasons from career_stats — reused for the selector
  // so no extra round-trip. '' = all seasons.
  seasonOptions: string[]
}

function fmtNum(v: number | null, decimals = 0): string {
  if (v == null) return '—'
  if (decimals === 0 && Number.isInteger(v)) return String(v)
  return v.toFixed(decimals || 1)
}

function fmtPct(v: number | null): string {
  if (v == null) return '—'
  const pct = v * 100
  return `${Number.isInteger(pct) ? pct : pct.toFixed(1)}%`
}

// "5-9 · 55.6%" | "13-6 · —" (invalid pct) | "—" (no data at all)
function shootingCell(
  makes: number | null,
  attempts: number | null,
  pct: number | null,
): string {
  if (makes == null && attempts == null) return '—'
  return `${makes ?? '—'}-${attempts ?? '—'} · ${fmtPct(pct)}`
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(`${iso}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { year: '2-digit', month: 'short', day: 'numeric' })
}

export default function GameLogSection({ playerSlug, seasonOptions }: Props) {
  const [season, setSeason] = useState('')
  const [rows, setRows] = useState<GameRow[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setErr(null)
    setOffset(0)
    const qs = season ? `?season=${encodeURIComponent(season)}` : ''
    fetch(`/api/players/${encodeURIComponent(playerSlug)}/games${qs}`)
      .then(async (res) => {
        if (cancelled) return
        if (!res.ok) throw new Error(`request_failed_${res.status}`)
        const data = (await res.json()) as GamesResponse
        if (cancelled) return
        setRows(data.games)
        setHasMore(data.has_more)
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'load_failed')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [playerSlug, season])

  async function loadMore() {
    if (loadingMore || !hasMore) return
    setLoadingMore(true)
    const nextOffset = offset + 50
    try {
      const qs = new URLSearchParams()
      if (season) qs.set('season', season)
      qs.set('offset', String(nextOffset))
      const res = await fetch(
        `/api/players/${encodeURIComponent(playerSlug)}/games?${qs.toString()}`,
      )
      if (!res.ok) throw new Error(`request_failed_${res.status}`)
      const data = (await res.json()) as GamesResponse
      setRows((prev) => [...prev, ...data.games])
      setHasMore(data.has_more)
      setOffset(nextOffset)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'load_failed')
    } finally {
      setLoadingMore(false)
    }
  }

  return (
    <section className="bg-hoop-surface border border-hoop-border rounded-2xl p-5">
      <header className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <h2 className="text-[12px] font-bold uppercase tracking-wider text-hoop-accent">
          Game Log
        </h2>
        {seasonOptions.length > 0 && (
          <select
            aria-label="Filter game log by season"
            value={season}
            onChange={(e) => setSeason(e.target.value)}
            className="bg-white dark:bg-hoop-surface border border-hoop-border rounded-[7px] px-[11px] py-1.5 text-[12.5px] text-hoop-text-2"
          >
            <option value="">All seasons</option>
            {seasonOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        )}
      </header>

      {err && <p className="text-sm text-hoop-red mb-2">{err}</p>}

      {loading ? (
        <p className="text-sm text-hoop-text-3">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-hoop-text-3">
          No per-game data for this player yet.
        </p>
      ) : (
        <>
          <div className="overflow-x-auto -mx-1">
            <table className="w-full min-w-[720px] text-[12.5px]">
              <thead>
                <tr className="text-left text-hoop-text-3 border-b border-hoop-border">
                  <th className="sticky left-0 bg-hoop-surface z-10 py-2 px-2 font-medium text-left" scope="col">
                    Date
                  </th>
                  <th className="py-2 px-2 font-medium text-left" scope="col">Opp</th>
                  <th className="py-2 px-2 font-medium text-right" scope="col">MIN</th>
                  <th className="py-2 px-2 font-medium text-right" scope="col">PTS</th>
                  <th className="py-2 px-2 font-medium text-right" scope="col">REB</th>
                  <th className="py-2 px-2 font-medium text-right" scope="col">AST</th>
                  <th className="py-2 px-2 font-medium text-right" scope="col">FG</th>
                  <th className="py-2 px-2 font-medium text-right" scope="col">3PT</th>
                  <th className="py-2 px-2 font-medium text-right" scope="col">FT</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((g) => (
                  <tr
                    key={`${g.api_game_id}`}
                    className="border-b border-hoop-border last:border-b-0 text-hoop-text"
                  >
                    <td className="sticky left-0 bg-hoop-surface z-10 py-2 px-2 whitespace-nowrap">
                      {fmtDate(g.date)}
                    </td>
                    <td className="py-2 px-2 whitespace-nowrap">
                      {g.opponent ? (
                        <>
                          <span className="text-hoop-text-3">
                            {g.is_home ? 'vs ' : '@ '}
                          </span>
                          {g.opponent}
                        </>
                      ) : (
                        <span className="text-hoop-text-3">
                          {[g.home_team, g.away_team].filter(Boolean).join(' vs ') || '—'}
                        </span>
                      )}
                      {g.league && (
                        <span className="text-hoop-text-3 text-[10.5px]"> · {g.league}</span>
                      )}
                      {g.is_3x3 && (
                        <span className="ml-1.5 px-1 py-0.5 rounded text-[9.5px] font-semibold bg-hoop-accent/10 text-hoop-accent border border-hoop-accent/40">
                          3x3
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtNum(g.minutes, 1)}</td>
                    <td className="py-2 px-2 text-right tabular-nums font-semibold">{fmtNum(g.pts)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtNum(g.reb)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtNum(g.ast)}</td>
                    <td className="py-2 px-2 text-right tabular-nums whitespace-nowrap">
                      {shootingCell(
                        g.fg2m != null && g.tpm != null ? g.fg2m + g.tpm : null,
                        g.fg2a != null && g.tpa != null ? g.fg2a + g.tpa : null,
                        g.fg_pct,
                      )}
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums whitespace-nowrap">
                      {shootingCell(g.tpm, g.tpa, g.three_pct)}
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums whitespace-nowrap">
                      {shootingCell(g.ftm, g.fta, g.ft_pct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <div className="mt-3 text-center">
              <button
                type="button"
                onClick={() => void loadMore()}
                disabled={loadingMore}
                className="text-sm font-medium text-hoop-accent hover:underline disabled:opacity-40"
              >
                {loadingMore ? 'Loading…' : 'Load more games'}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  )
}
